import {Dot} from 'recharts';
import styled, {useTheme} from 'styled-components';

import {isNil} from 'ramda';

import {PriceReportType} from '../../../../../types/PriceReportType';
import {DIMMED_SCATTER_OPACITY} from '../consts/dimmedScatterOpacity';
import {MY_VEHICLE_RADIUS} from '../consts/myVehicleRadius';
import {SCATTER_SHAPE_TRANSITION} from '../consts/scatterShapeTransition';
import {useChartToolbar} from '../hooks/useChartToolbar';
import {StatisticsChartItem} from '../types/StatisticsChartItem';
import {getChartItemColor} from '../utils/getChartItemColor';
import {getScatterDotStroke} from '../utils/getScatterDotStroke';
import {
  getScatterDoughnutRingRadius,
  getScatterDoughnutStrokeWidth,
} from '../utils/getScatterDoughnutStrokeWidth';
import {getScatterPriceChangeDirection} from '../utils/getScatterPriceChangeDirection';
import {
  getScatterTrianglePoints,
  getScatterTriangleRingPath,
} from '../utils/getScatterTrianglePoints';
import {isPrivateSeller} from '../utils/isPrivateSeller';
import {isVehicleInHighlightedGroup} from '../utils/isVehicleInHighlightedGroup';
import {MyVehicleShape} from './MyVehicleShape';

const DOT_RADIUS = 8;
const TRIANGLE_SIZE_OFFSET = 1;

interface ScatterShapeProps {
  cx?: number; // Passed by recharts
  cy?: number; // Passed by recharts
  x?: number; // Passed by recharts
  width?: number; // Passed by recharts
  y?: number; // Passed by recharts
  payload?: StatisticsChartItem;
  priceReport: PriceReportType;
  activeTooltipIndex: number | null;
  selectedVehicleIds: string[] | null;
}

export function ScatterShape(props: ScatterShapeProps) {
  const theme = useTheme();
  const {highlights, effectiveHighlightMode, selectedSellerId} = useChartToolbar();

  const isMyVehicle = props.payload?.car.isMyVehicle;
  const isMyAd = props.payload?.car.isMyAd;
  const dotRadius = isMyAd ? MY_VEHICLE_RADIUS : DOT_RADIUS;

  if (isMyVehicle) {
    return <MyVehicleShape {...props} chartType="scatter" />;
  }

  const color = props.payload
    ? getChartItemColor(
        theme,
        props.priceReport,
        props.payload?.car,
        props.activeTooltipIndex === props.payload?.index,
        !!props.selectedVehicleIds?.includes(props.payload.car.ad_id)
      )
    : undefined;

  const {stroke, strokeOpacity, strokeWidth} = getScatterDotStroke({
    theme,
    car: props.payload?.car,
    highlights,
    isMyAd: !!isMyAd,
    selectedSellerId,
  });

  const priceChangeDirection =
    highlights.repriced && props.payload
      ? getScatterPriceChangeDirection(props.payload.car, props.priceReport, true)
      : null;
  const showPrivateSellerDoughnut =
    highlights.sellerType && props.payload && isPrivateSeller(props.payload.car);

  const isInHighlightedGroup = props.payload
    ? isVehicleInHighlightedGroup(
        props.payload.car,
        highlights,
        props.priceReport,
        selectedSellerId
      )
    : true;
  const isHidden = effectiveHighlightMode === 'hideOthers' && !isInHighlightedGroup;
  const isDimmed = effectiveHighlightMode === 'dimOthers' && !isInHighlightedGroup;

  let fillOpacity = 1;
  let dimmedStrokeOpacity = strokeOpacity;

  if (isHidden) {
    fillOpacity = 0;
    dimmedStrokeOpacity = 0;
  } else if (isDimmed) {
    fillOpacity = DIMMED_SCATTER_OPACITY;
    dimmedStrokeOpacity = DIMMED_SCATTER_OPACITY;
  }

  if (!isNil(props.cx) && !isNil(props.cy) && priceChangeDirection) {
    const triangleSize = dotRadius + TRIANGLE_SIZE_OFFSET;

    if (showPrivateSellerDoughnut) {
      const outerTrianglePoints = getScatterTrianglePoints(
        props.cx,
        props.cy,
        priceChangeDirection,
        triangleSize
      );

      return (
        <>
          <TriangleRingWithHover
            d={getScatterTriangleRingPath(props.cx, props.cy, priceChangeDirection, triangleSize)}
            fill={color}
            fillRule="evenodd"
            fillOpacity={fillOpacity}
            pointerEvents={isHidden ? 'none' : undefined}
          />
          {strokeWidth ? (
            <TriangleBorder
              points={outerTrianglePoints}
              fill="none"
              stroke={stroke}
              strokeOpacity={dimmedStrokeOpacity}
              strokeWidth={strokeWidth}
              pointerEvents="none"
            />
          ) : null}
        </>
      );
    }

    return (
      <TriangleWithHover
        points={getScatterTrianglePoints(props.cx, props.cy, priceChangeDirection, triangleSize)}
        fill={color}
        fillOpacity={fillOpacity}
        stroke={stroke}
        strokeOpacity={dimmedStrokeOpacity}
        strokeWidth={strokeWidth}
        pointerEvents={isHidden ? 'none' : undefined}
      />
    );
  }

  if (!isNil(props.cx) && !isNil(props.cy) && showPrivateSellerDoughnut) {
    return (
      <>
        <DoughnutWithHover
          cx={props.cx}
          cy={props.cy}
          r={getScatterDoughnutRingRadius(dotRadius)}
          fill="none"
          stroke={color}
          strokeWidth={getScatterDoughnutStrokeWidth(dotRadius)}
          strokeOpacity={fillOpacity}
          pointerEvents={isHidden ? 'none' : undefined}
        />
        {strokeWidth ? (
          <DoughnutBorder
            cx={props.cx}
            cy={props.cy}
            r={dotRadius}
            fill="none"
            stroke={stroke}
            strokeOpacity={dimmedStrokeOpacity}
            strokeWidth={strokeWidth}
            pointerEvents="none"
          />
        ) : null}
      </>
    );
  }

  return (
    <DotWithHover
      {...props}
      r={dotRadius}
      fill={color}
      fillOpacity={fillOpacity}
      stroke={stroke}
      strokeOpacity={dimmedStrokeOpacity}
      strokeWidth={strokeWidth}
      pointerEvents={isHidden ? 'none' : undefined}
    />
  );
}

const TriangleWithHover = styled.polygon`
  transition: ${SCATTER_SHAPE_TRANSITION};

  &:hover {
    fill: ${({theme}) => theme.colors.palettes.blue['40']['100']};
  }
`;

const TriangleRingWithHover = styled.path`
  transition: ${SCATTER_SHAPE_TRANSITION};

  &:hover {
    fill: ${({theme}) => theme.colors.palettes.blue['40']['100']};
  }
`;

const TriangleBorder = styled.polygon`
  transition: ${SCATTER_SHAPE_TRANSITION};
`;

const DoughnutWithHover = styled.circle`
  transition: ${SCATTER_SHAPE_TRANSITION};

  &:hover {
    stroke: ${({theme}) => theme.colors.palettes.blue['40']['100']};
  }
`;

const DoughnutBorder = styled.circle`
  transition: ${SCATTER_SHAPE_TRANSITION};
`;

// ScatterChart has different onMouseMove prop (there is missing activeTooltipIndex attribute)
const DotWithHover = styled(Dot)`
  transition: ${SCATTER_SHAPE_TRANSITION};

  &:hover {
    fill: ${({theme}) => theme.colors.palettes.blue['40']['100']};
  }
`;
